import api from './api';
import requestQueue, { RequestPriority } from '../utils/request-queue';
import cache from '../utils/cache';

export interface TrainingCatalogQuery {
  search?: string;
  format?: 'presentiel' | 'en_ligne' | 'hybride';
  specialties?: string[];
  subcategories?: string[];
  fields?: string[];
  wilaya?: string;
  centerName?: string;
  level?: string;
  priceMin?: number;
  priceMax?: number;
  durationMin?: number;
  durationMax?: number;
  location?: string;
  rating?: number;
  dateFrom?: string;
  dateTo?: string;
  availability?: 'available' | 'full' | 'soon';
  sortBy?: 'newest' | 'popularity' | 'price-low' | 'price-high' | 'rating' | 'duration' | 'upcoming';
  viewMode?: 'grid' | 'list';
  page?: number;
  limit?: number;
}

export interface TrainingResponse {
  id: string;
  title: string;
  description: string;
  price: number;
  duration: number;
  duration_unit?: string; // e.g., 'hours', 'days', 'weeks'
  format: string;
  center: {
    id: number;
    name: string;
    location: string;
    logo?: string;
  };
  views: number;
  rating: number;
  reviews: number;
  image?: string;
  specialties: string[];
  startDate?: string;
  endDate?: string;
  maxParticipants?: number;
  currentParticipants?: number;
  scheduleDate: string;
  fullDescription?: string;
  learningObjectives?: string;
  skillsGained?: string;
  targetAudience?: string;
  trainer?: string;
  language?: string;
  country?: string;
  city?: string;
  reference?: string;
  level?: string;
}

export interface TrainingCatalogResponse {
  data: TrainingResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TrainingStats {
  totalTrainings: number;
  totalCenters: number;
  totalFields: number;
  averageRating: number;
  totalViews: number;
  totalTrainees: number;
}

class TrainingCatalogService {
  /**
   * Generate cache key from query parameters
   */
  private getCacheKey(endpoint: string, query: TrainingCatalogQuery): string {
    const sortedQuery = Object.keys(query)
      .sort()
      .reduce((acc, key) => {
        acc[key] = query[key as keyof TrainingCatalogQuery];
        return acc;
      }, {} as any);

    return `${endpoint}_${JSON.stringify(sortedQuery)}`;
  }

  async getCatalog(query: TrainingCatalogQuery = {}): Promise<TrainingCatalogResponse> {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const response = await api.get(`/public/trainings/catalog?${params.toString()}`);
    return response.data;
  }

  async getAllTrainings(query: TrainingCatalogQuery = {}, config?: any): Promise<TrainingCatalogResponse> {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const endpoint = `/public/trainings?${params.toString()}`;
    const cacheKey = this.getCacheKey('trainings', query);

    // Check cache first
    const cached = cache.get<TrainingCatalogResponse>(cacheKey);
    if (cached) {
      return cached;
    }

    // Use request queue to prevent overwhelming HTTP/2 connection
    const response = await requestQueue.enqueue(
      () => api.get(endpoint, config),
      RequestPriority.HIGH // Training searches are high priority
    );

    // Cache the result for 2 minutes
    cache.set(cacheKey, response.data, 2 * 60 * 1000);

    return response.data;
  }

  async getTrainingById(id: string): Promise<TrainingResponse> {
    const response = await api.get(`/public/trainings/${id}`);
    return response.data;
  }

  async getStats(): Promise<TrainingStats> {
    const response = await api.get('/public/trainings/stats');
    return response.data;
  }

  async getSpecialties(): Promise<string[]> {
    try {
      const response = await api.get('/public/trainings/specialties');
      return response.data && response.data.length > 0 ? response.data : [];
    } catch (error) {
      return [];
    }
  }

  async getLocations(): Promise<string[]> {
    try {
      const response = await api.get('/public/trainings/locations');
      return response.data && response.data.length > 0 ? response.data : [];
    } catch (error) {
      return [];
    }
  }

  async getCategories(): Promise<{ id: string; name: string }[]> {
    try {
      const response = await api.get('/public/trainings/categories');
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  async getSubcategories(categoryId?: string): Promise<{ id: string; name: string }[]> {
    try {
      const params = categoryId ? { categoryId } : {};
      const response = await api.get('/public/trainings/subcategories', { params });
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  async getFields(subcategoryId?: string): Promise<{ id: string; name: string }[]> {
    try {
      const params = subcategoryId ? { subcategoryId } : {};
      const response = await api.get('/public/trainings/fields', { params });
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  async getLevels(): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get('/public/trainings/levels');
      return response.data || [];
    } catch (error) {
      return [];
    }
  }

  async getFormats(): Promise<{ value: string; label: string }[]> {
    // Return static formats to avoid performance issues with fetching all trainings
    // This resolves the production timeout issue
    return [
      { value: 'presentiel', label: 'Présentiel' },
      { value: 'en_ligne', label: 'En ligne' },
      { value: 'hybride', label: 'Hybride' }
    ];
  }

  async getWilayas(): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get('/wilayas');
      const wilayas = response.data || [];

      return wilayas.map((wilaya: any) => ({
        value: wilaya.name.toLowerCase().replace(/\s+/g, '_'),
        label: wilaya.name
      }));
    } catch (error) {
      // Fallback to default wilayas
      return [
        { value: 'alger', label: 'Alger' },
        { value: 'oran', label: 'Oran' },
        { value: 'constantine', label: 'Constantine' },
        { value: 'annaba', label: 'Annaba' },
        { value: 'blida', label: 'Blida' }
      ];
    }
  }
}

export const trainingCatalogService = new TrainingCatalogService(); 